import type { CarouselLlmProgress, CarouselLlmSegment } from "@/lib/api";

const DEFAULT_MS = 28_000;

export const COPY_JOB_STEPS = ["discussion", "copy", "review"] as const;

export const STEP_BAR: Record<string, { label: string; bar: string; track: string }> = {
  discussion: { label: "Role", bar: "bg-violet-500", track: "bg-violet-200" },
  copy: { label: "Slajdy", bar: "bg-sky-500", track: "bg-sky-200" },
  review: { label: "Recenzja", bar: "bg-emerald-500", track: "bg-emerald-200" },
  concepts: { label: "Koncepcje", bar: "bg-violet-500", track: "bg-violet-200" },
  retro: { label: "Retro", bar: "bg-amber-500", track: "bg-amber-200" },
  draft_place: { label: "Wstawianie", bar: "bg-sky-500", track: "bg-sky-200" },
  draft_caption: { label: "Opis", bar: "bg-amber-500", track: "bg-amber-200" },
  caption_cta: { label: "CTA", bar: "bg-rose-500", track: "bg-rose-200" },
  layout_plan: { label: "Układ", bar: "bg-slate-500", track: "bg-slate-200" },
  draft_critique: { label: "Krytyka", bar: "bg-orange-500", track: "bg-orange-200" },
  design_review: { label: "Analiza", bar: "bg-rose-500", track: "bg-rose-200" },
  design_verify: { label: "Weryfikacja", bar: "bg-emerald-500", track: "bg-emerald-200" },
};

const DRAFT_STEP_IDS = new Set(["layout_plan", "draft_critique", "draft_place", "draft_caption"]);

function normalizeStepId(id?: string): string {
  if (id === "discuss") return "discussion";
  return id || "";
}

function isCopyTrio(steps: string[]): boolean {
  return steps.length === COPY_JOB_STEPS.length && COPY_JOB_STEPS.every((id, i) => steps[i] === id);
}

export function isCopyPipeline(step?: string, llmStatus?: string, knownSteps: string[] = []): boolean {
  if (knownSteps.some((id) => DRAFT_STEP_IDS.has(id))) return false;
  if (isCopyTrio(knownSteps)) return true;
  const status = (llmStatus || "").replace("discuss", "discussion");
  const current = normalizeStepId(step);
  return current === "discussion" || current === "copy" || status === "discussion" || status === "copy";
}

export function resolveJobSteps(
  progress?: CarouselLlmProgress | null,
  llmStatus?: string,
): string[] {
  const fromPayload = (progress?.job_steps || []).map((id) => normalizeStepId(id)).filter(Boolean);
  if (fromPayload.length > 1) return fromPayload;
  const fromSegs = (progress?.segments || [])
    .map((row) => normalizeStepId(row.id))
    .filter(Boolean);
  if (fromSegs.length > 1) return fromSegs;
  const known = fromPayload.length ? fromPayload : fromSegs;
  if (isCopyPipeline(progress?.step, llmStatus, known)) return [...COPY_JOB_STEPS];
  if (fromPayload.length) return fromPayload;
  if (progress?.step) return [normalizeStepId(progress.step)];
  return [];
}

function elapsedMs(iso?: string, now = Date.now()): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, now - t);
}

function stepMeta(id: string): { label: string; bar: string; track: string } {
  return STEP_BAR[id] || { label: id, bar: "bg-brand", track: "bg-muted" };
}

export function jobSegments(
  progress: CarouselLlmProgress | null | undefined,
  now = Date.now(),
  llmStatus?: string,
): CarouselLlmSegment[] {
  const steps = resolveJobSteps(progress, llmStatus);
  const raw = steps.map((id) => {
    const existing = (progress?.segments || []).find((row) => (row.id === "discuss" ? "discussion" : row.id) === id);
    return {
      id,
      label: existing?.label || stepMeta(id).label,
      weight_ms: existing?.weight_ms || 1,
      fraction: existing?.fraction || 0,
    };
  });
  if (!raw.length) return [];
  const phase = progress?.phase || "waiting";
  const current = progress?.step || raw[0]?.id || "";
  const received = Math.max(0, Number(progress?.received_chars || 0));
  const expectC = Math.max(1, Number(progress?.expected_chars || 1));
  const stepElapsed = elapsedMs(progress?.step_started_at || progress?.awaiting_text_since || progress?.started_at, now);
  const ids = raw.map((row) => row.id || "");
  const curI = Math.max(0, ids.indexOf(current));
  return raw.map((row, i) => {
    const weight = Math.max(1, Number(row.weight_ms || 1));
    let fraction = 0;
    if (phase === "done") fraction = 1;
    else if (i < curI) fraction = 1;
    else if (i > curI) fraction = 0;
    else if (received <= 0) fraction = Math.min(0.28, stepElapsed / weight);
    else fraction = Math.min(0.99, Math.max(stepElapsed / weight, received / expectC));
    return {
      id: row.id || current,
      label: row.label || stepMeta(row.id || current).label,
      weight_ms: weight,
      fraction: Number(fraction.toFixed(4)),
    };
  });
}

export function estimateLlmProgress(
  progress: CarouselLlmProgress | null | undefined,
  now = Date.now(),
  llmStatus?: string,
): {
  percent: number;
  eta_sec: number;
  elapsed_sec: number;
  expected_ms: number;
  segments: CarouselLlmSegment[];
  awaiting_text_sec: number;
} {
  const phase = progress?.phase || "waiting";
  const received = Math.max(0, Number(progress?.received_chars || 0));
  const expectT = Math.max(1, Number(progress?.job_expected_ms || progress?.expected_ms || DEFAULT_MS));
  const elapsed = elapsedMs(progress?.started_at, now);
  const awaitingSince = progress?.awaiting_text_since;
  const awaiting = received <= 0 && phase !== "done"
    ? Math.max(
        0,
        Math.round(
          (awaitingSince ? elapsedMs(awaitingSince, now) : elapsedMs(progress?.updated_at || progress?.started_at, now)) /
            1000,
        ),
      )
    : 0;
  const segments = jobSegments(progress, now, llmStatus);
  const totalW = segments.reduce((sum, row) => sum + Math.max(1, row.weight_ms || 1), 0);
  const filled = segments.reduce((sum, row) => sum + Math.max(1, row.weight_ms || 1) * Number(row.fraction || 0), 0);
  let pct = phase === "done" ? 100 : Math.max(1, Math.min(99, Math.round((100 * filled) / Math.max(totalW, 1))));
  if (!segments.length) {
    const expectC = Math.max(1, Number(progress?.expected_chars || 1));
    const timePct = Math.min(92, (100 * elapsed) / expectT);
    const sizePct = (100 * received) / expectC;
    pct = received <= 0 ? Math.min(28, (100 * elapsed) / expectT) : Math.min(99, Math.max(timePct, sizePct));
    if (phase === "done") pct = 100;
  }
  const timeEta = Math.max(0, (expectT - elapsed) / 1000);
  return {
    percent: phase === "done" ? 100 : Math.max(1, Math.min(99, Math.round(pct))),
    eta_sec: Math.max(0, Math.round(timeEta)),
    elapsed_sec: Math.max(0, Math.round(elapsed / 1000)),
    expected_ms: expectT,
    segments,
    awaiting_text_sec: awaiting,
  };
}
